import { config, helpers } from "aaa";
import { numberToCurrencyString } from "util/helpers";

export interface IAmount {
    value: number; // eurocent - depending on precision
    precision: number;
    currency: string;
}

export const ZERO_AMOUNT = {
    value: 0,
    precision: 2,
    currency: "EUR"
};

export class Amount {
    amount: IAmount;

    get number(): number {
        return amountToNumber(this.amount);
    }

    set number(value: number) {
        amountSetValue(this.amount, value);
    }

    get remainder(): number {
        return amountRemainder(this.amount);
    }

    get currencyString(): string {
        return amountToCurrencyString(this.amount);
    }

    constructor(amount: IAmount) {
        this.amount = { ...amount };
    }

    clone(): Amount {
        return new Amount({ ...this.amount });
    }

    add(amount: IAmount, multiplier?: number): Amount {
        let rhs = amount;
        if (multiplier) {
            rhs = amountMultiply(amount, multiplier);
        }
        return new Amount(amountAdd(this.amount, rhs));
    }

    subtract(amount: IAmount): Amount {
        return new Amount(amountSubtract(this.amount, amount));
    }

    multiply(multiplier: number): Amount {
        return new Amount(amountMultiply(this.amount, multiplier));
    }

    clamp(min: IAmount, max: IAmount): Amount {
        if (this.number > amountToNumber(max)) {
            return new Amount(max);
        } else if (this.number < amountToNumber(min)) {
            return new Amount(min);
        } else {
            return this.clone();
        }
    }
}

export function amountSetValue(amount: IAmount, value: number): IAmount {
    const factor = Math.pow(10, amount.precision);
    amount.value = value * factor;
    return amount;
}

export function amountChangePrecision(amount: IAmount, precision: number): IAmount {
    if (precision === amount.precision) {
        return amount;
    }

    const diff = precision - amount.precision;
    const mult = Math.pow(10, diff);
    return {
        value: amount.value * mult,
        precision: precision,
        currency: amount.currency
    };
}

/**
 * Add two amounts (currency MUST be same)
 * Precision will be the bigger of the two
 */
export function amountAdd(lhs: IAmount, rhs: IAmount, sign: 1 | -1 = 1): IAmount {
    if (lhs.currency !== rhs.currency) {
        throw Error("Adding amounts with different currency");
    }

    const precision = Math.max(lhs.precision, rhs.precision);
    lhs = amountChangePrecision(lhs, precision);
    rhs = amountChangePrecision(rhs, precision);

    return {
        value: lhs.value + sign * rhs.value,
        precision: precision,
        currency: lhs.currency
    };
}

/**
 * Add two amounts (currency MUST be same)
 * Precision will be the bigger of the two
 */
export function amountSubtract(lhs: IAmount, rhs: IAmount): IAmount {
    return amountAdd(lhs, rhs, -1);
}

export function amountMultiply(lhs: IAmount, multiplier: number): IAmount {
    let ret = { ...lhs };
    ret.value *= multiplier;
    return ret;
}

/**
 * Given an amount 123456.89  returns "€123.456,89" (locale = de)
 */
export function amountToCurrencyString(amount?: IAmount, integerOnly = false, symbolInFront = true): string {
    if (!amount) {
        return "";
    }

    const divider = Math.pow(10, amount.precision);
    const value = amount.value / divider;

    return numberToCurrencyString(value, amount.currency, integerOnly, symbolInFront);
}

export function amountToNumber(amount: IAmount): number {
    const divider = Math.pow(10, amount.precision);
    return amount.value / divider;
}

/**
 * Remainder of an IAmount
 */
export function amountRemainder(amount: IAmount): number {
    const divider = Math.pow(10, amount.precision);
    return amount.value % divider;
}

export function numberToEuroAmount(value: number): IAmount {
    return { value: value * 100, precision: 2, currency: "EUR" };
}



// WEBPACK FOOTER //
// ./src/types/amount.ts